<script setup lang="ts">
/**
 * 3D phone frame. Pass the screen content via the default slot.
 *
 * Props:
 *  - tilt: 'left' | 'right' | 'center' | 'none' — base static tilt
 *  - floaty: bool — slow up/down float
 *  - parallax: bool — react to mouse position (desktop only)
 *  - delay: ms — animation start offset (so multiple phones don't move in sync)
 */
const props = withDefaults(defineProps<{
  tilt?:     'left' | 'right' | 'center' | 'none'
  floaty?:   boolean
  parallax?: boolean
  delay?:    number
}>(), {
  tilt:     'center',
  floaty:   true,
  parallax: false,
  delay:    0,
})

const tiltClass = computed(() => {
  switch (props.tilt) {
    case 'left':   return 'rod-phone--tilt-left'
    case 'right':  return 'rod-phone--tilt-right'
    case 'center': return 'rod-phone--tilt-center'
    default:       return ''
  }
})

const phoneEl = ref<HTMLElement | null>(null)
const transform = ref<string>('')

const onMove = (e: MouseEvent) => {
  if (!props.parallax || !phoneEl.value) return
  const r = phoneEl.value.getBoundingClientRect()
  const cx = r.left + r.width  / 2
  const cy = r.top  + r.height / 2
  const dx = (e.clientX - cx) / r.width
  const dy = (e.clientY - cy) / r.height
  // Combine static tilt + parallax delta
  const baseY = props.tilt === 'left' ? 18 : props.tilt === 'right' ? -18 : props.tilt === 'center' ? -8 : 0
  const baseX = props.tilt === 'none' ? 0 : 5
  transform.value = `rotateY(${baseY + dx * 14}deg) rotateX(${baseX - dy * 10}deg)`
}

onMounted(() => {
  if (props.parallax && window.matchMedia('(min-width: 1024px)').matches) {
    window.addEventListener('mousemove', onMove, { passive: true })
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMove)
})
</script>

<template>
  <div class="rod-scene">
    <div
      ref="phoneEl"
      class="rod-phone"
      :class="[tiltClass, floaty && 'rod-phone--floaty']"
      :style="{
        animationDelay: delay ? `${delay}ms` : undefined,
        transform: parallax ? transform || undefined : undefined,
        transition: parallax ? 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
      }"
    >
      <div class="rod-phone__notch" />
      <div class="rod-phone__screen">
        <slot />
      </div>
    </div>
  </div>
</template>
